import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Button,
  Card,
  CardMedia,
  Rating,
  Chip,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  TextField,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Close,
  Add,
  Remove,
  Star,
  AccessTime,
  Favorite,
  FavoriteBorder,
} from '@mui/icons-material';
import { useRestaurant } from '../../contexts/RestaurantContext';
import { useNotification } from '../../contexts/NotificationContext';
import { useCustomTheme } from '../../contexts/ThemeContext';
import { getImageProps } from '../../utils/imageUtils';

const MenuItemModal = ({ item, open, onClose, onAddToCart, viewOnly = false }) => {
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [isFavorite, setIsFavorite] = useState(false);

  const theme = useTheme();
  const { isDarkMode } = useCustomTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { addToCart } = useRestaurant();
  const { showSuccess } = useNotification();

  // Reset state when modal opens with new item
  React.useEffect(() => {
    if (item && open) {
      const defaultSize = item.sizes_with_prices?.find(size => size.is_default) || item.sizes_with_prices?.[0];
      setSelectedSize(defaultSize || null);
      setSelectedAddons([]);
      setQuantity(1);
      setSpecialInstructions('');
    }
  }, [item, open]);

  if (!item) return null;

  const handleSizeChange = (event) => {
    const size = item.sizes_with_prices?.find(s => s.name === event.target.value);
    setSelectedSize(size || null);
  };

  const handleAddonChange = (addon, checked) => {
    if (checked) {
      setSelectedAddons(prev => [...prev, { ...addon, quantity: 1 }]);
    } else {
      setSelectedAddons(prev => prev.filter(a => a.name !== addon.name));
    }
  };

  const handleAddonQuantityChange = (addonName, newQuantity) => {
    if (newQuantity <= 0) {
      setSelectedAddons(prev => prev.filter(a => a.name !== addonName));
    } else {
      setSelectedAddons(prev =>
        prev.map(a => a.name === addonName ? { ...a, quantity: newQuantity } : a)
      );
    }
  };

  const calculateTotalPrice = () => {
    let price = item?.base_price || 0;
    
    // Apply item discount if enabled
    if (item?.discount_enabled && item?.discount_percentage > 0) {
      price = price * (1 - item.discount_percentage / 100);
    }
    
    if (selectedSize) price = selectedSize.final_price || price;
    if (selectedAddons?.length) {
      price += selectedAddons.reduce((sum, addon) => sum + ((addon?.price || 0) * (addon?.quantity || 1)), 0);
    }
    return price * quantity;
  };

  const handleAddToCart = () => {
    if (onAddToCart) {
      // Use custom onAddToCart if provided
      onAddToCart(item, quantity, selectedAddons, specialInstructions, selectedSize);
    } else if (addToCart) {
      // Fallback to context addToCart
      addToCart(item, quantity, selectedAddons, specialInstructions, selectedSize);
    }
    if (showSuccess) {
      showSuccess(`${item?.name || 'Item'} added to cart!`);
    }
    onClose();
  };

  const totalPrice = calculateTotalPrice();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 2,
          maxHeight: isMobile ? '100vh' : '85vh',
          height: isMobile ? '100%' : 'auto',
          margin: isMobile ? 0 : 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        }
      }}
    >
      <DialogContent sx={{ p: 0, '&::-webkit-scrollbar': { display: 'none' } }}>
        {/* Header */}
        <Box sx={{ position: 'relative' }}>
          <CardMedia
            component="img"
            height={isMobile ? 200 : 220}
            {...getImageProps(item?.image_url, 'menu_item', item?.name)}
            sx={{ objectFit: 'cover' }}
          />
          
          {/* Close Button */}
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              bgcolor: 'rgba(255, 255, 255, 0.9)',
              '&:hover': { bgcolor: 'rgba(255, 255, 255, 1)' },
            }}
          >
            <Close />
          </IconButton>

          {/* Favorite Button */}
          <IconButton
            onClick={() => setIsFavorite(!isFavorite)}
            sx={{
              position: 'absolute',
              top: 16,
              left: 16,
              bgcolor: 'rgba(255, 255, 255, 0.9)',
              '&:hover': { bgcolor: 'rgba(255, 255, 255, 1)' },
            }}
          >
            {isFavorite ? <Favorite color="error" /> : <FavoriteBorder />}
          </IconButton>

          {/* Veg/Non-veg Indicator */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 16,
              left: 16,
              width: 24,
              height: 24,
              border: `2px solid ${item?.is_veg ? theme.palette.success.main : theme.palette.error.main}`,
              bgcolor: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                bgcolor: item?.is_veg ? theme.palette.success.main : theme.palette.error.main,
              }}
            />
          </Box>

          {/* Spicy Indicator */}
          {item?.is_spicy && (
            <Chip label="🌶️ Spicy" size="small" variant="outlined" color="warning" />
          )}
        </Box>

        <Box sx={{ p: { xs: 2.5, sm: 3 }, overflowY: 'auto', flexGrow: 1 }}>
          {/* Item Info */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ color: 'text.primary', mb: 1.5 }}>
              {item?.name || 'Menu Item'}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Rating value={item?.rating || 0} readOnly size="small" precision={0.1} />
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '14px' }}>
                  ({item?.rating || 0})
                </Typography>
              </Box>
              {item?.preparation_time > 0 && 
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <AccessTime sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '14px' }}>
                  {item?.preparation_time} min
                </Typography>
              </Box> 
              }
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
              {item?.description || 'No description available.'}
            </Typography>

            {/* Ingredients */}
            {item?.ingredients && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" fontWeight="600" sx={{ mb: 1, color: 'text.primary' }}>
                  Ingredients
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                  {item.ingredients}
                </Typography>
              </Box>
            )}

            {/* Allergens */}
            {item?.allergens && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" fontWeight="600" sx={{ mb: 1, color: 'text.primary' }}>
                  Allergens
                </Typography>
                <Typography variant="body2" color="error.main" sx={{ lineHeight: 1.6 }}>
                  {item.allergens}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Price Display */}
          <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            {item?.discount_enabled && item?.discount_percentage > 0 ? (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      textDecoration: 'line-through', 
                      color: 'text.secondary',
                      fontSize: '14px'
                    }}
                  >
                    S${item.base_price}
                  </Typography>
                  <Chip
                    label={`${item.discount_percentage}% OFF`}
                    size="small"
                    color="error"
                    sx={{ 
                      fontWeight: 600,
                      fontSize: '13px',
                      height: 22
                    }}
                  />
                </Box>
                <Typography variant="h5" color="error" fontWeight="bold" sx={{ mb: 0.5 }}>
                  S${(item.base_price * (1 - item.discount_percentage / 100)).toFixed(2)}
                </Typography>
                <Typography variant="body2" color="success.main" sx={{ fontSize: '14px', fontWeight: 500 }}>
                  You save S${(item.base_price * (item.discount_percentage / 100)).toFixed(2)}!
                </Typography>
              </Box>
            ) : (
              <Typography variant="h5" color="primary" fontWeight="bold">
                S${item?.base_price || 0}
              </Typography>
            )}
          </Box>

          {/* Size Selection */}
          {item?.sizes_with_prices && item.sizes_with_prices.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" fontWeight="700" gutterBottom sx={{ fontSize: '14px', color: 'text.primary', mb: 1.5 }}>
                Choose Size
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {item.sizes_with_prices.map((size) => {
                  const isSelected = selectedSize?.name === size?.name;
                  
                  return (
                    <Box
                      key={size?.id}
                      onClick={() => handleSizeChange({ target: { value: size?.name } })}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 1.5,
                        border: '1px solid',
                        borderColor: isSelected ? 'primary.main' : 'divider',
                        borderRadius: 1,
                        bgcolor: isSelected ? 'primary.50' : 'background.paper',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': { 
                          bgcolor: isSelected ? 'primary.50' : 'action.hover',
                          borderColor: isSelected ? 'primary.main' : 'primary.light'
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: '50%',
                            border: '2px solid',
                            borderColor: isSelected ? 'primary.main' : 'divider',
                            bgcolor: isSelected ? 'primary.main' : 'transparent',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s ease-in-out'
                          }}
                        >
                          {isSelected && (
                            <Box
                              sx={{
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                bgcolor: 'white'
                              }}
                            />
                          )}
                        </Box>
                        <Box>
                          <Typography variant="body2" sx={{ fontSize: '14px', fontWeight: isSelected ? 600 : 400 }}>
                            {size?.name}
                          </Typography>
                          {size?.is_default && (
                            <Chip 
                              label="Default" 
                              size="small" 
                              color="primary"
                              sx={{ fontSize: '16px', height: 20, mt: 0.5 }}
                            />
                          )}
                        </Box>
                      </Box>
                      <Typography variant="body2" color="primary.main" fontWeight="600" sx={{ fontSize: '14px' }}>
                        S${size?.final_price || 0}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          {/* Add-ons Selection */}
          {item?.addons_with_details && item.addons_with_details.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" fontWeight="700" gutterBottom sx={{ fontSize: '14px', color: 'text.primary', mb: 1.5 }}>
                Add-ons (Optional)
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {item.addons_with_details.map((addon) => {
                  const isSelected = selectedAddons.some(a => a.name === addon.name);
                  const selectedAddon = selectedAddons.find(a => a.name === addon.name);
                  const quantity = selectedAddon?.quantity || 0;
                  
                  return (
                  <Box
                    key={addon?.name}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 1,
                      border: '1px solid',
                        borderColor: isSelected ? 'primary.main' : 'divider',
                      borderRadius: 1,
                        bgcolor: isSelected ? 'primary.50' : 'background.paper',
                        '&:hover': { bgcolor: 'action.hover' }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Checkbox
                          checked={isSelected}
                          onChange={(e) => handleAddonChange(addon, e.target.checked)}
                          size="small"
                        />
                    <Box>
                      <Typography variant="body2" sx={{ fontSize: '14px' }}>{addon?.name}</Typography>
                      {addon?.description && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '14px' }}>
                          {addon.description}
                        </Typography>
                      )}
                    </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {isSelected && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <IconButton
                              onClick={() => handleAddonQuantityChange(addon.name, quantity - 1)}
                              size="small"
                              sx={{ width: 24, height: 24 }}
                            >
                              <Remove sx={{ fontSize: 12 }} />
                            </IconButton>
                            <Typography variant="body2" sx={{ minWidth: 20, textAlign: 'center', fontSize: '14px' }}>
                              {quantity}
                            </Typography>
                            <IconButton
                              onClick={() => handleAddonQuantityChange(addon.name, quantity + 1)}
                              size="small"
                              sx={{ width: 24, height: 24 }}
                            >
                              <Add sx={{ fontSize: 12 }} />
                            </IconButton>
                          </Box>
                        )}
                    <Typography variant="body2" color="primary.main" fontWeight="600" sx={{ fontSize: '14px' }}>
                      S${addon?.price || 0}
                    </Typography>
                  </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          {/* Special Instructions */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" fontWeight="700" gutterBottom sx={{ fontSize: '14px', color: 'text.primary', mb: 1.5 }}>
              Special Instructions
            </Typography>
            <TextField
              fullWidth
              placeholder="Any special requests for this item..."
              multiline
              rows={3}
              value={specialInstructions}
              onChange={(e) => setSpecialInstructions(e.target.value)}
              variant="outlined"
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                    borderWidth: 2,
                  },
                }
              }}
            />
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Quantity and Price - Only show if not viewOnly */}
          {!viewOnly && (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: 'center', 
              justifyContent: 'space-between', 
              mb: 3,
              gap: 2,
              p: 2,
              bgcolor: 'grey.50',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'grey.200'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="subtitle1" fontWeight="700" sx={{ fontSize: '14px', color: 'text.primary' }}>
                  Quantity:
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <IconButton
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    sx={{ 
                      bgcolor: 'primary.main', 
                      color: 'white',
                      width: 36, 
                      height: 36,
                      '&:hover': { bgcolor: 'primary.dark' }
                    }}
                    size="small"
                  >
                    <Remove sx={{ fontSize: 16 }} />
                  </IconButton>
                  <Typography variant="h6" sx={{ minWidth: 32, textAlign: 'center', fontWeight: 600 }}>
                    {quantity}
                  </Typography>
                  <IconButton
                    onClick={() => setQuantity(quantity + 1)}
                    sx={{ 
                      bgcolor: 'primary.main', 
                      color: 'white',
                      width: 36, 
                      height: 36,
                      '&:hover': { bgcolor: 'primary.dark' }
                    }}
                    size="small"
                  >
                    <Add sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              </Box>

              <Typography variant="h4" fontWeight="bold" color="primary.main">
                S${totalPrice.toFixed(2)}
              </Typography>
            </Box>
          )}

          {/* Price Display for View Only */}
          {viewOnly && (
            <Box sx={{ mb: 2, textAlign: 'center' }}>
              <Typography variant="h6" fontWeight="bold" color="primary.main">
                S${totalPrice.toFixed(2)}
              </Typography>
            </Box>
          )}

          {/* Add to Cart Button or View Only Message */}
          {viewOnly ? (
            <Box sx={{ textAlign: 'center', py: 1 }}>
              <Typography variant="body1" color="primary.main" gutterBottom>
                Choose Delivery or Takeaway to Order
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Visit our Delivery or Takeaway sections to add this item to your cart
              </Typography>
            </Box>
          ) : (
            <Button
              variant="contained"
              fullWidth
              size="large"
              onClick={handleAddToCart}
              sx={{
                borderRadius: 3,
                textTransform: 'none',
                fontWeight: 700,
                py: 1.5,
                fontSize: '16px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                '&:hover': {
                  boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
                  transform: 'translateY(-1px)',
                },
                transition: 'all 0.2s ease-in-out'
              }}
            >
              Add to Cart - S${totalPrice.toFixed(2)}
            </Button>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default MenuItemModal;
